import { storeToRefs } from "pinia";
import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import { useAcl } from "vue-simple-acl";
import {
  PATH_ERROR,
  PATH_NOT_FOUND,
  PATH_UNAUTHORIZED,
} from "~/core/constants/paths";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { canWithRedirection } from "~/core/utils/canWithRedirection";
import ErrorNotFoundPage from "~/core/views/ErrorNotFoundPage.vue";
import ErrorUnauthorizedPage from "~/core/views/ErrorUnauthorizedPage.vue";
import ErrorUnexpectedPage from "~/core/views/ErrorUnexpectedPage.vue";
import aboutRoutes from "~/modules/about/routes";
import applicationManagementRoutes from "~/modules/applicationManagement/routes";
import {
  PATH_BUSINESS_UNITS,
  PATH_BUSINESS_UNITS_ID,
} from "~/modules/businessUnit/constants";
import businessUnitRoutes from "~/modules/businessUnit/routes";
import campaignRoutes from "~/modules/campaign/routes";
import cartographyRoutes from "~/modules/cartography/routes";
import connectMobileAppRoutes from "~/modules/connectMobileApp/routes";
import { PATH_DASHBOARD } from "~/modules/dashboard/constants";
import dashboardRoutes from "~/modules/dashboard/routes";
import { PATH_DTWINS } from "~/modules/dtwin/constants";
import dtwinRoutes from "~/modules/dtwin/routes";
import firmwareRoutes from "~/modules/firmware/routes";
import fleetRoutes from "~/modules/fleet/routes";
import globalDeviceConfigurationRoutes from "~/modules/globalDeviceConfiguration/routes";
import { useFetchActiveLicenseInformation } from "~/modules/license/composables/useFetchActiveLicenseInformation";
import { LicenseFeatureName } from "~/modules/license/models/LicenseFeatureName.enum";
import licenseRoutes from "~/modules/license/routes";
import { useLicenseStore } from "~/modules/license/stores/useLicenseStore";
import logRoutes from "~/modules/log/routes";
import { PATH_ORGANIZATIONS_ID } from "~/modules/organization/constants";
import organizationRoutes from "~/modules/organization/routes";
import sectorRoutes from "~/modules/sector/routes";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { PATH_SUPER_ADMINISTRATION } from "~/modules/superAdministration/constants";
import superAdministrationRoutes from "~/modules/superAdministration/routes";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import userRoutes from "~/modules/user/routes";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";

const { can } = useAcl();

const routes: RouteRecordRaw[] = ([] as RouteRecordRaw[]).concat(
  aboutRoutes,
  applicationManagementRoutes,
  businessUnitRoutes,
  campaignRoutes,
  dashboardRoutes,
  cartographyRoutes,
  connectMobileAppRoutes,
  dtwinRoutes,
  firmwareRoutes,
  fleetRoutes,
  globalDeviceConfigurationRoutes,
  licenseRoutes,
  logRoutes,
  organizationRoutes,
  sectorRoutes,
  superAdministrationRoutes,
  userRoutes,
  [
    {
      path: PATH_ERROR,
      component: ErrorUnexpectedPage,
      meta: {
        layout: "ErrorUnexpectedLayout",
      },
    },
    {
      path: PATH_UNAUTHORIZED,
      component: ErrorUnauthorizedPage,
      meta: {
        layout: "ErrorUnauthorizedLayout",
      },
    },
    {
      path: PATH_NOT_FOUND,
      component: ErrorNotFoundPage,
      meta: {
        layout: "ErrorNotFoundLayout",
      },
    },
  ],
);

/**
 * Set up the layouts for each route.
 * Replace the package used before: https://www.npmjs.com/package/vite-plugin-vue-layouts#how-it-works
 * @param routes
 */
function setupLayouts(routes: RouteRecordRaw[]) {
  const routesSetupLayouts = [];
  const mapRoutesByLayout: Map<string, RouteRecordRaw[]> = new Map();

  for (const route of routes) {
    // set route layout to DefaultLayout if not defined
    route.meta = {
      ...(route.meta || {}),
      layout: route.meta?.layout || "DefaultLayout",
    };

    let layoutRoutes = mapRoutesByLayout.get(route.meta.layout!);

    if (!layoutRoutes) {
      layoutRoutes = [];
      mapRoutesByLayout.set(route.meta.layout!, layoutRoutes);
    }

    layoutRoutes.push(route);
  }

  for (const [layout, routes] of mapRoutesByLayout.entries()) {
    routesSetupLayouts.push({
      path: "",
      component: () => import(`~/core/views/${layout}.vue`),
      children: routes,
    });
  }

  return routesSetupLayouts;
}

/**
 * Set the name of the routes if not set.
 * This enables to use the method "resolve" of the router with the name corresponding to the path.
 * Example: router.resolve({ name: <route.path>, params: { <param1>: <value1>, <param2>: <value2> }})
 * @param routes
 */
function setupNames(routes: RouteRecordRaw[]) {
  for (const route of routes) {
    route.name = route.name ?? route.path;

    if (route.children && route.children.length) {
      route.children = setupNames(route.children);
    }
  }

  return routes;
}

export const router = createRouter({
  history: createWebHistory(),
  routes: setupLayouts(setupNames(routes)),
});

/**
 * Manage route checks
 * Set current user if needed and clear code/session_state query params
 * Return false if not auth after current user should have been set to avoid showing part of application before auto redirecting to keycloak login
 */
router.beforeEach(
  async (
    to: RouteLocationNormalized,
  ): Promise<RouteLocationNormalized | boolean | void> => {
    const { setCurrentUser, isAuthenticated } = useUserSession();

    if ((to.meta.auth === undefined || to.meta.auth) && !isAuthenticated) {
      await setCurrentUser();

      delete to.query.code;
      delete to.query.session_state;
      // set isAuthenticated again to fix infinite loading time
      const { isAuthenticated } = useUserSession();

      if (isAuthenticated) {
        return to;
      } else {
        return false;
      }
    }

    // redirection depending on the user's organization type
    const isErrorPage = to.meta.layout?.startsWith("Error");

    if (!isErrorPage) {
      const { userSession } = storeToRefs(useUserSession());
      const { fetchActiveLicenseInformation } =
        useFetchActiveLicenseInformation({
          organizationCode: userSession.value?.entity.code,
        });
      const { isLicenseFeatureEnabled } = useLicenseStore();

      switch (userSession.value?.entity.type) {
        // a user of the super administration can only access to the administration pages
        case UserSessionEntityType.SUPER_ADMINISTRATION:
          if (to.path === PATH_DASHBOARD) {
            await router.replace(PATH_SUPER_ADMINISTRATION);
          } else if (to.meta.layout !== "AdministrationLayout") {
            await router.replace(PATH_UNAUTHORIZED);
          }
          break;
        // a user of a business unit can only access to the administration pages except the super administration ones
        case UserSessionEntityType.BUSINESS_UNIT:
          if (to.path === PATH_DASHBOARD) {
            await router.replace({
              name: PATH_BUSINESS_UNITS_ID,
              params: { id: userSession.value.entity.code },
            });
          } else if (
            to.path.startsWith(PATH_SUPER_ADMINISTRATION) ||
            to.meta.layout !== "AdministrationLayout"
          ) {
            await router.replace(PATH_UNAUTHORIZED);
          }
          break;
        // a user of an administration organization can only access to the administration pages except the super administration and the business units ones
        case UserSessionEntityType.ADMINISTRATION:
          if (to.path === PATH_DASHBOARD) {
            await router.replace({
              name: PATH_ORGANIZATIONS_ID,
              params: { id: userSession.value.entity.code },
            });
          } else if (
            to.path.startsWith(PATH_SUPER_ADMINISTRATION) ||
            to.path.startsWith(PATH_BUSINESS_UNITS) ||
            to.meta.layout !== "AdministrationLayout"
          ) {
            await router.replace(PATH_UNAUTHORIZED);
          }
          break;
        // a user of an operation organization can only access to the operation pages
        // and the organization administration if he has the permissions
        case UserSessionEntityType.OPERATOR:
          // initialize the sector store which is a precondition to use the application
          // with an authenticated user and the left menu
          if (isAuthenticated && to.meta.layout === "DefaultLayout") {
            try {
              const sectorsStore = useSectorStore();
              await sectorsStore.init(to.query);
            } catch (error) {
              await router.push(PATH_ERROR);
              console.error(error);
            }
          }

          await fetchActiveLicenseInformation();

          if (
            to.path.startsWith(PATH_SUPER_ADMINISTRATION) ||
            to.path.startsWith(PATH_BUSINESS_UNITS)
          ) {
            await router.replace(PATH_UNAUTHORIZED);
          }
          // the permissions for dashboard access are only checked at this moment and not before
          // in order to manage the other users landing page
          else if (to.path === PATH_DASHBOARD) {
            await canWithRedirection(can, ACLRoles.CAN_READ_DEVICE);
            if (!isLicenseFeatureEnabled(LicenseFeatureName.DASHBOARD_FULL)) {
              await router.replace(PATH_DTWINS);
            }
          }
          break;
      }
    }

    // case checking display confirm
    const confirmLeavePageStore = useConfirmLeavePageStore();

    // need confirm before leaving the page
    if (confirmLeavePageStore.isActivated(to)) {
      // let redirection pass if it is done after confirm
      if (confirmLeavePageStore.confirmed()) {
        confirmLeavePageStore.reset();
      }
      // otherwise display the leaving page confirm message
      else {
        // provide the destination route in order to redirect after confirm
        confirmLeavePageStore.show(to.fullPath);
        return false; // stop redirection for confirm
      }
    }
  },
);
